/**
 * Hook moderne pour la gestion des abonnements et tokens
 * Basé sur la documentation API backend mise à jour
 * ✨ Avec cache IndexedDB pour les plans et abonnements
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  SubscriptionPlan,
  CustomerTokenBalance,
  TokenTransaction,
  Subscription,
  CreateSubscriptionData,
  GetPlansParams,
  GetTokenTransactionsParams,
  CustomerType,
  EffectiveStatusResponse,
  GracePeriodInfo
} from '../types/subscription';

import modernSubscriptionService from '../services/modernSubscription';
import { cacheService } from '../services/cacheService';
import { useToast } from './useToast';

export interface UseModernSubscriptionReturn {
  // State moderne
  subscription: Subscription | null;
  plans: SubscriptionPlan[];
  tokenBalance: CustomerTokenBalance | null;
  tokenTransactions: TokenTransaction[];
  isLoading: boolean;
  error: string | null;
  
  // ✨ État pour Freemium
  isFreemium: boolean;
  hasSubscription: boolean;

  // Actions enrichies
  loadSubscription: () => Promise<void>;
  loadPlans: (params?: GetPlansParams) => Promise<void>;
  loadTokenBalance: () => Promise<void>;
  loadTokenTransactions: (params?: GetTokenTransactionsParams) => Promise<void>;
  createSubscription: (data: CreateSubscriptionData) => Promise<void>;
  initializeFreemium: (customerId: string) => Promise<void>; // ✨ Nouvelle action
  
  // Utilitaires enrichis
  getPlansByType: (customerType: CustomerType) => SubscriptionPlan[];
  getDefaultPlan: (customerType: CustomerType) => SubscriptionPlan | null;
  getMonthlyCost: (plan: SubscriptionPlan) => number;
  getAnnualCost: (plan: SubscriptionPlan) => number;
  getAnnualSavings: (plan: SubscriptionPlan) => number;
  formatTokenAmount: (amount: number) => string;
  getRemainingTokensPercentage: () => number;

  // === COMPATIBILITÉ UI EXISTANTE ===
  getReceiptData: () => ReceiptCompatData | null;
  getTokenRingData: () => TokenRingCompatData;
  getLegacySubscriptionData: () => LegacySubscriptionData | null;
  getLegacyPlansData: () => LegacyPlanData[];
  
  // === PROPRIÉTÉS ENRICHIES ===
  smePlans: SubscriptionPlan[];
  fiPlans: SubscriptionPlan[];
  getAnnualPrice: (plan: SubscriptionPlan) => number;
  
  // === 🆕 STATUT EFFECTIF (effective-status) ===
  effectiveStatus: EffectiveStatusResponse | null;
  gracePeriod: GracePeriodInfo | null;
  isInGracePeriod: boolean;
  isDowngraded: boolean;
  statusMessage: string;
  loadEffectiveStatus: () => Promise<void>;
}

// Types de compatibilité pour les UI existantes
export interface ReceiptCompatData {
  payment: {
    id: string;
    date: string;
    amount: number;
    method: string;
    plan: string;
    status: string;
    receiptUrl?: string;
  };
}

export interface TokenRingCompatData {
  tokenBalance: number;
  tokenTotal: number;
}

export interface LegacySubscriptionData {
  id: string;
  userId: string;
  currentPlan: string;
  tokenBalance: number;
  tokenTotal: number;
  paymentHistory: any[];
}

export interface LegacyPlanData {
  name: string;
  description: string;
  price: number;
  features: string[];
  highlighted?: boolean;
  demo?: boolean;
  tokenAmount?: string;
}

export const useModernSubscription = (): UseModernSubscriptionReturn => {
  // State
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [tokenBalance, setTokenBalance] = useState<CustomerTokenBalance | null>(null);
  const [tokenTransactions, setTokenTransactions] = useState<TokenTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // 🆕 State pour le statut effectif
  const [effectiveStatus, setEffectiveStatus] = useState<EffectiveStatusResponse | null>(null);
  
  // ✅ Ref pour éviter les appels multiples
  const initialLoadDone = useRef(false);
  
  const toast = useToast();
  // ✅ Ref stable pour toast - évite les boucles infinies dans useCallback
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // ✨ Refs pour le cache
  const plansLoadedRef = useRef<boolean>(false);
  const lastPlansLoadRef = useRef<number>(0);

  // ============================================
  // 📦 CHARGEMENT DES PLANS DEPUIS INDEXEDDB (CACHE-FIRST)
  // ============================================

  const loadPlans = useCallback(async (params?: GetPlansParams) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // ✨ ÉTAPE 1: Essayer de charger depuis IndexedDB d'abord
      if (!plansLoadedRef.current) {
        const cachedPlans = await cacheService.get<SubscriptionPlan[]>('plans', 'all');
        if (cachedPlans && cachedPlans.data && cachedPlans.data.length > 0) {
          console.log('📦 [Plans] Chargés depuis IndexedDB:', cachedPlans.data.length, 'plans');
          setPlans(cachedPlans.data);
          plansLoadedRef.current = true;
          setIsLoading(false);
          
          // ✨ Vérifier si le cache est encore frais (1 heure)
          if (cacheService.isCacheValid(cachedPlans.metadata)) {
            console.log('✅ [Plans] Cache encore valide, pas de revalidation nécessaire');
            return;
          }
          console.log('🔄 [Plans] Cache expiré, revalidation en background...');
        }
      }
      
      // ✨ ÉTAPE 2: Charger depuis le backend
      const now = Date.now();
      // Anti-rebond: minimum 30 secondes entre les appels API pour les plans
      if (now - lastPlansLoadRef.current < 30000 && plansLoadedRef.current) {
        console.log('⏳ [Plans] Revalidation ignorée (trop rapprochée)');
        return;
      }
      lastPlansLoadRef.current = now;
      
      console.log('🌐 [Plans] Chargement depuis le backend...');
      const data = await modernSubscriptionService.getSubscriptionPlans(params);
      setPlans(data);
      plansLoadedRef.current = true;
      
      // ✨ Sauvegarder dans IndexedDB pour la prochaine fois
      await cacheService.set('plans', 'all', data, 'system', 'synced');
      console.log('💾 [Plans] Sauvegardés dans IndexedDB:', data.length, 'plans');
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('❌ [Plans] Erreur chargement:', error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load functions
  const loadSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await modernSubscriptionService.getCurrentSubscription();
      setSubscription(data);
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Impossible de charger l\'abonnement';
      setError(errorMessage);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Erreur abonnement',
        message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 🆕 Charge le statut effectif de l'abonnement
   * Inclut gracePeriod, isDowngraded, statusMessage
   */
  const loadEffectiveStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await modernSubscriptionService.getEffectiveStatus();
      setEffectiveStatus(data);
      // Synchroniser aussi l'abonnement si disponible
      if (data?.subscription) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Impossible de charger le statut effectif';
      setError(errorMessage);
      console.warn('⚠️ [EffectiveStatus] Fallback vers loadSubscription:', errorMessage);
      // Fallback: charger l'abonnement normal
      await loadSubscription();
    } finally {
      setIsLoading(false);
    }
  }, [loadSubscription]);

  const loadTokenBalance = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await modernSubscriptionService.getTokenBalance();
      setTokenBalance(data);
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Impossible de charger le solde de tokens';
      setError(errorMessage);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Erreur tokens',
        message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadTokenTransactions = useCallback(async (params?: GetTokenTransactionsParams) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await modernSubscriptionService.getTokenTransactions(params);
      setTokenTransactions(response.transactions);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Actions
  const createSubscription = useCallback(async (data: CreateSubscriptionData) => {
    try {
      setIsLoading(true);
      setError(null);
      const newSubscription = await modernSubscriptionService.createSubscription(data);
      setSubscription(newSubscription);
      
      toastRef.current.showToast?.({
        type: 'success',
        title: 'Paiement réussi',
        message: 'Votre abonnement a été activé avec succès !'
      });
      
      // Recharger le solde de tokens car l'abonnement peut modifier l'allocation
      await loadTokenBalance();
    } catch (err) {
      const error = err as Error;
      const errorMessage = error.message || 'Erreur lors du paiement';
      setError(errorMessage);
      
      toastRef.current.showToast?.({
        type: 'error',
        title: 'Erreur de paiement',
        message: errorMessage
      });
      
      throw error; // Re-throw pour permettre au composant de gérer l'erreur
    } finally {
      setIsLoading(false);
    }
  }, [loadTokenBalance]);

  // ✨ Initialiser un abonnement Freemium pour un nouveau compte
  const initializeFreemium = useCallback(async (customerId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const newSubscription = await modernSubscriptionService.initializeFreemiumSubscription(customerId);
      setSubscription(newSubscription);
      await loadTokenBalance();
      console.log('✅ Abonnement Freemium initialisé avec succès');
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('❌ Erreur initialisation Freemium:', error.message);
    } finally {
      setIsLoading(false);
    }
  }, [loadTokenBalance]);

  // Utility functions
  const getPlansByType = useCallback((customerType: CustomerType): SubscriptionPlan[] => {
    return plans.filter(plan => plan.customerType === customerType);
  }, [plans]);

  // ✨ Obtenir le plan par défaut selon le type de client
  const getDefaultPlan = useCallback((customerType: CustomerType): SubscriptionPlan | null => {
    const typePlans = plans.filter(plan => plan.customerType === customerType);
    
    // Chercher le plan marqué comme défaut
    const defaultPlan = typePlans.find(plan => plan.metadata?.isDefaultPlan === true);
    if (defaultPlan) return defaultPlan;
    
    // Fallback: Le plan gratuit pour SME
    if (customerType === CustomerType.SME) {
      return typePlans.find(plan => plan.monthlyPriceUSD === 0) || typePlans[0] || null;
    }
    
    return typePlans[0] || null;
  }, [plans]);

  const getMonthlyCost = useCallback((plan: SubscriptionPlan): number => {
    return plan.monthlyPriceUSD;
  }, []);

  const getAnnualCost = useCallback((plan: SubscriptionPlan): number => {
    return plan.annualPriceUSD;
  }, []);

  const getAnnualSavings = useCallback((plan: SubscriptionPlan): number => {
    const monthlyTotal = plan.monthlyPriceUSD * 12;
    return monthlyTotal - plan.annualPriceUSD;
  }, []);

  const formatTokenAmount = useCallback((amount: number): string => {
    // -1 signifie illimité
    if (amount === -1) {
      return 'Illimité';
    }
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    } else {
      return amount.toString();
    }
  }, []);

  const getRemainingTokensPercentage = useCallback((): number => {
    if (!tokenBalance || tokenBalance.totalTokens === 0) return 0;
    return (tokenBalance.remainingTokens / tokenBalance.totalTokens) * 100;
  }, [tokenBalance]);

  // === MÉTHODES DE COMPATIBILITÉ UI ===
  
  const getReceiptData = useCallback((): ReceiptCompatData | null => {
    if (!subscription) return null;

    return {
      payment: {
        id: subscription.id,
        date: subscription.startDate || subscription.currentPeriodStart || subscription.createdAt,
        amount: parseFloat(subscription.amount || '') || subscription.plan.monthlyPriceUSD,
        method: subscription.paymentMethod?.type === 'card' ? 'Carte bancaire' :
                subscription.paymentMethod?.type === 'mobile' ? 'Mobile Money' : 'Paiement manuel',
        plan: subscription.plan.name,
        status: subscription.status === 'active' ? 'Payé' : 'En attente',
        receiptUrl: `#/receipt/${subscription.id}`
      }
    };
  }, [subscription]);

  const getTokenRingData = useCallback((): TokenRingCompatData => {
    if (!tokenBalance) return { tokenBalance: 0, tokenTotal: 1 };

    return {
      tokenBalance: tokenBalance.remainingTokens,
      tokenTotal: tokenBalance.totalTokens
    };
  }, [tokenBalance]);

  const getLegacySubscriptionData = useCallback((): LegacySubscriptionData | null => {
    if (!subscription || !tokenBalance) return null;

    return {
      id: subscription.id,
      userId: subscription.customerId,
      currentPlan: subscription.plan.name,
      tokenBalance: tokenBalance.remainingTokens,
      tokenTotal: tokenBalance.totalTokens,
      paymentHistory: [] // À implémenter avec les transactions si nécessaire
    };
  }, [subscription, tokenBalance]);

  const getLegacyPlansData = useCallback((): LegacyPlanData[] => {
    return plans.map(plan => ({
      name: plan.name,
      description: plan.description,
      price: plan.monthlyPriceUSD,
      features: Object.values(plan.features).map(f => f.description).filter((desc): desc is string => Boolean(desc)),
      highlighted: plan.isPopular,
      demo: plan.monthlyPriceUSD === 0,
      tokenAmount: formatTokenAmount(plan.tokenAllocation.monthlyTokens)
    }));
  }, [plans, formatTokenAmount]);

  // Initial load
  useEffect(() => {
    // ✅ Protection contre les appels multiples
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    
    const initialLoad = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Charger les données essentielles en parallèle
        await Promise.all([
          loadSubscription(),
          loadPlans(),
          loadTokenBalance()
        ]);
      } catch (err) {
        const error = err as Error;
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    initialLoad();
  }, []); // ✅ Dépendances vides - s'exécute une seule fois

  // ✨ Calculer si l'utilisateur est sur le plan Freemium
  const isFreemium = subscription?.plan?.monthlyPriceUSD === 0 || 
                     subscription?.planId === 'sme-freemium' ||
                     subscription?.plan?.name?.toLowerCase() === 'freemium';
  
  const hasSubscription = subscription !== null;

  return {
    // State
    subscription,
    plans,
    tokenBalance,
    tokenTransactions,
    isLoading,
    error,
    
    // ✨ État Freemium
    isFreemium,
    hasSubscription,

    // Actions
    loadSubscription,
    loadPlans,
    loadTokenBalance,
    loadTokenTransactions,
    createSubscription,
    initializeFreemium, // ✨ Nouvelle action
    loadEffectiveStatus, // 🆕 Charger le statut effectif

    // Utilities
    getPlansByType,
    getDefaultPlan, // ✨ Nouvelle utility
    getMonthlyCost,
    getAnnualCost,
    getAnnualSavings,
    formatTokenAmount,
    getRemainingTokensPercentage,

    // Compatibilité UI
    getReceiptData,
    getTokenRingData,
    getLegacySubscriptionData,
    getLegacyPlansData,

    // Propriétés enrichies
    smePlans: plans.filter(p => p.customerType === CustomerType.SME),
    fiPlans: plans.filter(p => p.customerType === CustomerType.FINANCIAL_INSTITUTION),
    getAnnualPrice: getAnnualCost,
    
    // 🆕 Statut effectif (effective-status)
    effectiveStatus,
    gracePeriod: effectiveStatus?.gracePeriod || null,
    isInGracePeriod: effectiveStatus?.gracePeriod?.inGracePeriod || false,
    isDowngraded: effectiveStatus?.isDowngraded || false,
    statusMessage: effectiveStatus?.statusMessage || ''
  };
};

// Hook spécialisé pour les plans SME
export const useSMEPlans = () => {
  const { plans, loadPlans, isLoading, error } = useModernSubscription();
  
  const smePlans = plans.filter(plan => plan.customerType === CustomerType.SME);
  
  useEffect(() => {
    loadPlans({ customerType: CustomerType.SME });
  }, [loadPlans]);

  return {
    plans: smePlans,
    isLoading,
    error,
    refresh: () => loadPlans({ customerType: CustomerType.SME })
  };
};

// Hook spécialisé pour les plans Institution Financière
export const useFinancialInstitutionPlans = () => {
  const { plans, loadPlans, isLoading, error } = useModernSubscription();
  
  const fiPlans = plans.filter(plan => plan.customerType === CustomerType.FINANCIAL_INSTITUTION);
  
  useEffect(() => {
    loadPlans({ customerType: CustomerType.FINANCIAL_INSTITUTION });
  }, [loadPlans]);

  return {
    plans: fiPlans,
    isLoading,
    error,
    refresh: () => loadPlans({ customerType: CustomerType.FINANCIAL_INSTITUTION })
  };
};

// Hook pour la comparaison de plans
export const usePlanComparison = (planIds: string[]) => {
  const { plans } = useModernSubscription();
  
  const comparisonPlans = plans.filter(plan => planIds.includes(plan.id));
  
  const compareFeatures = useCallback(() => {
    if (comparisonPlans.length === 0) return {};
    
    // Créer un objet avec toutes les fonctionnalités uniques
    const allFeatures = new Set<string>();
    comparisonPlans.forEach(plan => {
      Object.keys(plan.features).forEach(feature => allFeatures.add(feature));
    });
    
    // Créer la matrice de comparaison
    const comparison: Record<string, Record<string, any>> = {};
    Array.from(allFeatures).forEach(feature => {
      comparison[feature] = {};
      comparisonPlans.forEach(plan => {
        comparison[feature][plan.id] = plan.features[feature] || { enabled: false };
      });
    });
    
    return comparison;
  }, [comparisonPlans]);

  return {
    plans: comparisonPlans,
    compareFeatures
  };
};
